import { DateTime } from "luxon";
import { QuestionnaireComponentTranslations } from "translations/types";
import { extractEngageCodeError } from "utils/engageCodeValidation";
import { extractHpvFlowError } from "utils/hpvFlowValidation";
import { getScreeningFlow } from "utils/session-storage";
import {
    isValidRAMQ,
    isPhoneNumberValid,
    isValidPostalCode,
} from "utils/validation";
import Answer, { extractLocaleAnswer } from "./answer";
import { SCREENING_FLOW } from "./screening-flow";

export type QuestionType =
    | "radio"
    | "checkbox"
    | "text"
    | "date"
    | "number"
    | "year";

export type QuestionSubtype =
    | "email"
    | "ramq"
    | "phone"
    | "zipCode"
    | "autocomplete";

export default interface Question {
    id: number;
    questionnaireId?: number;
    type: QuestionType;
    key: string;
    index: string;
    subType?: QuestionSubtype;
    optionalDisplayRef?: string;
    optionalDisplayAnswers?: string;
    isOptional?: boolean;
    frTranslation: string;
    enTranslation: string;
    hintFr?: string;
    hintEn?: string;
    hideRamqNotice?: boolean;
    validateHpvFlow?: boolean;
    maxlength?: number;
    answers: Answer[];
    answer: Pick<
        Answer,
        "rawValue" | "frTranslation" | "enTranslation" | "exclusive"
    >;
    selectedAnswers?: Answer[];
}

export const extractLocaleQuestion = (
    { frTranslation, enTranslation }: Question,
    language: string
): string => {
    return language === "en" ? enTranslation : frTranslation;
};

export function extractLocaleQuestionHint(
    question: Question,
    language: string
): string | undefined {
    return language === "en" ? question.hintEn : question.hintFr;
}

export const canSubmitQuestion = ({
    isOptional,
    answer,
    type,
}: Question): boolean => {
    if (isOptional === true || type === "checkbox") {
        return true;
    }
    if (answer && answer.rawValue && typeof answer.rawValue === "string") {
        return answer.rawValue.length > 0;
    }
    return false;
};

export const extractQuestionError = (
    question: Question,
    previousQuestion: Question,
    translations: { questionnaireComponent: QuestionnaireComponentTranslations }
): string | undefined => {
    const engageError = extractEngageCodeError(
        question,
        previousQuestion,
        translations
    );

    if (engageError) {
        return engageError;
    }

    const hpvFlowError = extractHpvFlowError(question, translations);

    if (hpvFlowError) {
        return hpvFlowError;
    }

    if (question.isOptional) {
        return;
    }

    const t = translations.questionnaireComponent;

    const noAnswer = !question.answer || !question.answer.rawValue;

    if (["radio", "date", "year"].indexOf(question.type) > -1 && noAnswer) {
        return t.selectRequiredFieldError;
    } else if (["number", "text"].indexOf(question.type) > -1) {
        if (
            question.subType === "ramq" &&
            question.answer.rawValue &&
            !isValidRAMQ(question.answer.rawValue)
        ) {
            return t.ramqNumberInvalidError;
        }
        if (
            question.subType === "phone" &&
            !isPhoneNumberValid(question.answer.rawValue)
        ) {
            return t.phoneInvalidError;
        }
        if (
            question.subType === "zipCode" &&
            !isValidPostalCode(noAnswer ? "" : question.answer.rawValue)
        ) {
            return t.zipCodeInvalidError;
        }
        if (
            question.subType === "autocomplete" &&
            (noAnswer ||
                question.answer.rawValue.length === 0 ||
                !question.answers
                    .map(({ rawValue }) => rawValue)
                    .includes(question.answer.rawValue))
        ) {
            return t.selectRequiredFieldError;
        }
        if (
            (noAnswer || question.answer.rawValue.length === 0) &&
            question.subType !== "ramq"
        ) {
            return t.requiredFieldError;
        }

        // Very specific use case for people under 15
    } else if (question.key === "birthDate" && question.type === "date") {
        const date = DateTime.fromISO(question.answer.rawValue);
        const now = DateTime.now();
        const diff = now.diff(date, ["years"]);

        const isHpvScreeningFlow = getScreeningFlow() === SCREENING_FLOW.HPV;

        if (isHpvScreeningFlow && diff.years < 25) {
            return t.underTwentyFiveError;
        }
        if (diff.years < 15) {
            return t.underFifteenError;
        }
    }

    return;
};

export interface UserQuestionnaireAnswer {
    optionalAnswerId?: number;
    questionId: number;
    rawValue: string;
}

export const mapAnswerToUserQuestionnaireAnswer = (
    { id: optionalAnswerId, rawValue }: Pick<Answer, "id" | "rawValue">,
    { id: questionId }: Question
): UserQuestionnaireAnswer => ({
    optionalAnswerId,
    questionId,
    rawValue,
});

export const extractAnswerFromQuestion = (
    question: Question
): UserQuestionnaireAnswer | UserQuestionnaireAnswer[] | undefined => {
    const { answer, selectedAnswers, type } = question;

    if (
        type === "checkbox" &&
        selectedAnswers &&
        Array.isArray(selectedAnswers)
    ) {
        return selectedAnswers.map((answer) =>
            mapAnswerToUserQuestionnaireAnswer(answer, question)
        );
    } else if (answer) {
        return mapAnswerToUserQuestionnaireAnswer(answer, question);
    }

    return undefined;
};

export const findAnswerFromValue = (
    { answers }: Question,
    value: string
): Answer | undefined => {
    return answers.find(({ rawValue }) => rawValue === value);
};

export const findAnswerFromLocaleAnswer = (
    { answers }: Question,
    localAnswer: string,
    language: string
): Answer | undefined => {
    return answers.find(
        (answer) => extractLocaleAnswer(answer, language) === localAnswer
    );
};

export const getYearsArray = (): string[] => {
    const now = new Date();
    const years: string[] = [];

    for (let i = 0; i < 20; i++) {
        now.setFullYear(now.getFullYear() - 1);
        years.push(`${now.getFullYear()}`);
    }

    return years;
};
